import * as BABYLON from '@babylonjs/core'

/** Babylon's studio-grade specular env — indoor IBL without shipping a local HDR. */
export const STUDIO_IBL_ENV_URL = 'https://assets.babylonjs.com/environments/environmentSpecular.env'

export interface WallPrintFixture {
  head: BABYLON.Mesh
  tip: BABYLON.Mesh
  headMat: BABYLON.StandardMaterial
  tipMat: BABYLON.StandardMaterial
}

/**
 * Ceiling-track can — the SpotLight must sit at the tip (aperture) and aim
 * at the print so the beam reads as coming out of the fixture, not as a
 * wall-wash blob behind the frame.
 */
export function createWallPrintFixture(scene: BABYLON.Scene, id: string): WallPrintFixture {
  const headMat = new BABYLON.StandardMaterial(`art-fx-head-mat-${id}`, scene)
  headMat.diffuseColor = BABYLON.Color3.FromHexString('#1a1a1f')
  headMat.specularColor = BABYLON.Color3.Black()
  headMat.maxSimultaneousLights = 8

  const tipMat = new BABYLON.StandardMaterial(`art-fx-tip-mat-${id}`, scene)
  tipMat.emissiveColor = BABYLON.Color3.FromHexString('#ffe1b0').scale(0.85)
  tipMat.disableLighting = true

  const head = BABYLON.MeshBuilder.CreateCylinder(
    `art-fx-head-${id}`,
    { diameterTop: 0.1, diameterBottom: 0.16, height: 0.2, tessellation: 12 },
    scene,
  )
  head.material = headMat
  head.isPickable = false

  const tip = BABYLON.MeshBuilder.CreateSphere(`art-fx-tip-${id}`, { diameter: 0.07 }, scene)
  tip.material = tipMat
  tip.isPickable = false

  return { head, tip, headMat, tipMat }
}

export interface PrintFixturePlacement {
  /** World position of the SpotLight (fixture aperture). */
  spotPos: BABYLON.Vector3
  /** Normalized direction from aperture toward the print. */
  direction: BABYLON.Vector3
}

/**
 * Museum track placement: can hangs from the ceiling a short step off the wall,
 * aperture aims at the print center. Returns spot origin + aim for the light.
 */
export function placeWallPrintFixture(
  fixture: WallPrintFixture,
  printCenter: BABYLON.Vector3,
  wallNormal: BABYLON.Vector3,
  ceilingY: number,
): PrintFixturePlacement {
  // Sit on the ceiling, ~0.65m into the room from the wall — close enough that
  // the cone hits the print face, far enough to avoid a flat wall wash.
  const lampPos = printCenter.add(wallNormal.scale(0.65))
  lampPos.y = ceilingY - 0.14

  const aim = printCenter.clone()
  const toward = aim.subtract(lampPos).normalize()

  fixture.head.position.copyFrom(lampPos)
  // Cylinder is Y-up — rotate so local +Y points along the beam (out of the can).
  const up = BABYLON.Vector3.Up()
  const axis = BABYLON.Vector3.Cross(up, toward)
  const dot = BABYLON.Scalar.Clamp(BABYLON.Vector3.Dot(up, toward), -1, 1)
  if (axis.lengthSquared() > 1e-8) {
    fixture.head.rotationQuaternion = BABYLON.Quaternion.RotationAxis(axis.normalize(), Math.acos(dot))
  } else {
    fixture.head.rotationQuaternion =
      dot < 0 ? BABYLON.Quaternion.RotationAxis(BABYLON.Vector3.Right(), Math.PI) : BABYLON.Quaternion.Identity()
  }

  const spotPos = lampPos.add(toward.scale(0.12))
  fixture.tip.position.copyFrom(spotPos)

  return { spotPos, direction: toward }
}

export function disposeWallPrintFixture(fixture: WallPrintFixture): void {
  fixture.head.dispose()
  fixture.tip.dispose()
  fixture.headMat.dispose()
  fixture.tipMat.dispose()
}

/** Tune a print SpotLight so it keys the artwork from the fixture aperture. */
export function configurePrintSpotLight(
  spot: BABYLON.SpotLight,
  spotPos: BABYLON.Vector3,
  direction: BABYLON.Vector3,
  intensity: number,
  diffuse: BABYLON.Color3,
): void {
  spot.position.copyFrom(spotPos)
  spot.direction.copyFrom(direction)
  // Tight museum cone — wide angles painted purple ellipses on the wall.
  spot.angle = Math.PI / 7
  spot.exponent = 40
  spot.intensity = intensity
  spot.diffuse = diffuse
  spot.specular = diffuse.scale(0.25)
  spot.range = 8
}

export interface OverheadDownLightSpec {
  /** Exact aperture position (at/under the fixture mesh). */
  position: BABYLON.Vector3
  diffuse: BABYLON.Color3
  intensity: number
  range: number
  /** Cone angle; default soft downward can. */
  angle?: number
}

/**
 * Downlight that originates at an overhead fixture and fires toward the floor —
 * not an omni PointLight that washes the whole volume.
 */
export function createOverheadDownLight(
  scene: BABYLON.Scene,
  name: string,
  spec: OverheadDownLightSpec,
): BABYLON.SpotLight {
  const light = new BABYLON.SpotLight(
    name,
    spec.position.clone(),
    new BABYLON.Vector3(0, -1, 0),
    spec.angle ?? Math.PI / 3.2,
    28,
    scene,
  )
  light.diffuse = spec.diffuse
  light.specular = spec.diffuse.scale(0.3)
  light.intensity = spec.intensity
  light.range = spec.range
  return light
}

/**
 * Optional indoor IBL for Studio (StandardMaterial path — floor gets a
 * soft reflection; full PBR rewrite is a later pass). Returns a disposer.
 */
export function applyStudioIbl(
  scene: BABYLON.Scene,
  floorMat: BABYLON.StandardMaterial,
  intensity = 0.55,
): () => void {
  const env = BABYLON.CubeTexture.CreateFromPrefilteredData(STUDIO_IBL_ENV_URL, scene)
  scene.environmentTexture = env
  scene.environmentIntensity = intensity
  floorMat.reflectionTexture = env
  floorMat.reflectionTexture.coordinatesMode = BABYLON.Texture.CUBIC_MODE
  floorMat.reflectionTexture.level = 0.22

  return () => {
    if (floorMat.reflectionTexture === env) {
      floorMat.reflectionTexture = null
    }
    if (scene.environmentTexture === env) {
      scene.environmentTexture = null
    }
    env.dispose()
  }
}
